import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from 'components/accordion/accordion';
import { mapConfigAtom } from 'pages/area-geometries/show/atoms';
import Map from 'components/map/map';
import { PolygonArea } from 'components/map';
import { buildPolygonStyle } from 'lib/map';
import styles from './area-map.module.scss';
import { useEffect, useState } from 'react';
import { useScenarioForm } from './contexts';
import type { Geometry } from 'geojson';
import commonStyles from './common.module.scss';
import { atom, useAtomValue } from 'jotai';
import type { MapRef } from 'react-map-gl';
import turfBbox from '@turf/bbox';

const BASE_STYLE = buildPolygonStyle({
  color: '#FFB784',
  fillOpacity: 0.15,
  lineOpacity: 0.6,
  lineWidth: 2,
});
const SELECTED_STYLE = buildPolygonStyle({
  color: '#FFAFD2',
  fillOpacity: 0.3,
  lineOpacity: 0.6,
  lineWidth: 2,
});

const dcaMapRefAtom = atom<MapRef | null>(null);

export const AreaMap = () => {
  const [expanded, setExpanded] = useState(true);
  const { selectedConfig, configs } = useScenarioForm();
  const mapRef = useAtomValue(dcaMapRefAtom);

  const selectedAreaGeoJson = selectedConfig?.areaGeometryGeoJson as
    Geometry | null | undefined;

  // Focus the map on the selected config's area whenever the user picks a
  // config or the area geometry resolves.
  useEffect(() => {
    if (!mapRef) return;
    if (!selectedAreaGeoJson) return;

    const [minLng, minLat, maxLng, maxLat] = turfBbox(selectedAreaGeoJson);

    if (
      !Number.isFinite(minLng) ||
      !Number.isFinite(minLat) ||
      !Number.isFinite(maxLng) ||
      !Number.isFinite(maxLat)
    ) {
      return;
    }

    const map = mapRef.getMap();
    const frame = requestAnimationFrame(() => {
      map.fitBounds(
        [
          [minLng, minLat],
          [maxLng, maxLat],
        ],
        {
          padding: 40,
          maxZoom: 15,
          duration: 800,
          linear: true,
          essential: true,
        }
      );
    });
    return () => cancelAnimationFrame(frame);
  }, [mapRef, selectedConfig?.id, selectedAreaGeoJson]);

  return (
    <Accordion
      expanded={expanded}
      onClick={() => setExpanded(!expanded)}
      className={styles.accordion}
    >
      <AccordionSummary>
        <h2 className={commonStyles.configFormTitle}>Map</h2>
      </AccordionSummary>
      <AccordionDetails>
        <div className={styles.mapContainer}>
          <Map
            className={styles.mapFill}
            longitude={-98}
            latitude={31}
            zoom={4}
            mapConfigAtom={mapConfigAtom}
            refAtom={dcaMapRefAtom}
            showControls
            showMapFilters
          >
            {configs
              .filter((c) => c.areaGeometryGeoJson)
              .map((c) => (
                <PolygonArea
                  key={c.id}
                  id={`dca-area-${c.id}`}
                  geometry={c.areaGeometryGeoJson as Geometry}
                  style={
                    c.id === selectedConfig?.id ? SELECTED_STYLE : BASE_STYLE
                  }
                />
              ))}
          </Map>
        </div>
      </AccordionDetails>
    </Accordion>
  );
};
